import { createPortal } from 'react-dom'
import StatusBadge from './StatusBadge'
import { getStatusBadgeFromBackend } from '@/services/api/shipments'

interface ShipmentDetailsData {
  containerNumber: string
  shippingLine: string
  origin: string
  destination: string
  etd: string
  eta: string
  status: string
  bookingNumber?: string
  vesselName?: string
  voyageNumber?: string
  cargoDescription?: string
}

interface ShipmentDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  shipmentData?: ShipmentDetailsData
}

const ShipmentDetailsModal = ({ isOpen, onClose, shipmentData }: ShipmentDetailsModalProps) => {
  const statusBadgeProps = getStatusBadgeFromBackend(shipmentData?.status)

  if (!isOpen || !shipmentData) return null

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end"
      onClick={onClose}
      style={{ zIndex: 60 }}
    >
      <div 
        className="bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: 'Lexend Deca',
          width: '450px',
          maxWidth: '450px',
          height: '100vh',
          zIndex: 70,
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px'
        }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between border-b border-gray-200 flex-shrink-0"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2',
            borderTopLeftRadius: '12px',
            paddingLeft: '24px',
            paddingRight: '24px'
          }}
        >
          <h2 
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: '18px',
              lineHeight: '26px',
              letterSpacing: '0px',
              color: '#212126'
            }}
          >
            Shipment Details
          </h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center hover:opacity-80 transition-opacity"
            style={{ 
              width: '24px',
              height: '24px'
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Outer circle */}
              <circle
                cx="12"
                cy="12"
                r="10.5"
                stroke="#212126"
                strokeWidth="1.5"
                fill="none"
              />
              {/* Inner X - top-left to bottom-right */}
              <line
                x1="9.17"
                y1="9.17"
                x2="14.83"
                y2="14.83"
                stroke="#212126"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              {/* Inner X - top-right to bottom-left */}
              <line
                x1="14.83"
                y1="9.17"
                x2="9.17"
                y2="14.83"
                stroke="#212126"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Details Content */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="space-y-4">
            {/* Container Number */}
            <div className="flex items-center py-3 border-b border-gray-100" style={{ gap: '40px' }}>
              <span 
                className="flex-shrink-0"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A',
                  width: '140px'
                }}
              >
                Container Number
              </span>
              <span 
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#39553A'
                }}
              >
                {shipmentData.containerNumber}
              </span>
            </div>

            {/* Shipping Line */}
            <div className="flex items-center py-3 border-b border-gray-100" style={{ gap: '40px' }}>
              <span 
                className="flex-shrink-0"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A',
                  width: '140px'
                }}
              >
                Shipping Line
              </span>
              <span 
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '20px',
                  letterSpacing: '0px',
                  color: '#212126'
                }}
              >
                {shipmentData.shippingLine}
              </span>
            </div>

            {/* Origin */}
            <div className="flex items-center py-3 border-b border-gray-100" style={{ gap: '40px' }}>
              <span 
                className="flex-shrink-0"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A',
                  width: '140px'
                }}
              >
                Origin
              </span>
              <span 
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '20px',
                  letterSpacing: '0px',
                  color: '#212126'
                }}
              >
                {shipmentData.origin}
              </span>
            </div>

            {/* Destination */}
            <div className="flex items-center py-3 border-b border-gray-100" style={{ gap: '40px' }}>
              <span 
                className="flex-shrink-0"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A',
                  width: '140px'
                }}
              >
                Destination
              </span>
              <span 
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '20px',
                  letterSpacing: '0px',
                  color: '#212126'
                }}
              >
                {shipmentData.destination}
              </span>
            </div>

            {/* ETD */}
            <div className="flex items-center py-3 border-b border-gray-100" style={{ gap: '40px' }}>
              <span 
                className="flex-shrink-0"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A',
                  width: '140px'
                }}
              >
                ETD
              </span>
              <span 
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '20px',
                  letterSpacing: '0px',
                  color: '#212126'
                }}
              >
                {shipmentData.etd}
              </span>
            </div>

            {/* ETA */}
            <div className="flex items-center py-3 border-b border-gray-100" style={{ gap: '40px' }}>
              <span 
                className="flex-shrink-0"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A',
                  width: '140px'
                }}
              >
                ETA
              </span>
              <span 
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '20px',
                  letterSpacing: '0px',
                  color: '#212126'
                }}
              >
                {shipmentData.eta}
              </span>
            </div>

            {/* Status */}
            <div className="flex items-center py-3 border-b border-gray-100" style={{ gap: '40px' }}>
              <span 
                className="flex-shrink-0"
                style={{ 
                  fontFamily: 'Lexend Deca',
                  fontWeight: 300,
                  fontStyle: 'normal',
                  fontSize: '14px',
                  lineHeight: '22px',
                  letterSpacing: '0px',
                  color: '#69697A',
                  width: '140px'
                }}
              >
                Status
              </span>
              <StatusBadge 
                status={statusBadgeProps.status} 
                label={statusBadgeProps.label} 
              />
            </div>

            {/* Booking Number */}
            {shipmentData.bookingNumber && (
              <div className="flex items-center py-3 border-b border-gray-100" style={{ gap: '40px' }}>
                <span 
                  className="flex-shrink-0"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 300,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '22px',
                    letterSpacing: '0px',
                    color: '#69697A',
                    width: '140px'
                  }}
                >
                  Booking Number
                </span>
                <span 
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 500,
                    fontStyle: 'normal',
                    fontSize: '12px',
                    lineHeight: '20px',
                    letterSpacing: '0px',
                    color: '#212126'
                  }}
                >
                  {shipmentData.bookingNumber}
                </span>
              </div>
            )}

            {/* Vessel Name */}
            {shipmentData.vesselName && (
              <div className="flex items-center py-3 border-b border-gray-100" style={{ gap: '40px' }}>
                <span 
                  className="flex-shrink-0"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 300,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '22px',
                    letterSpacing: '0px',
                    color: '#69697A',
                    width: '140px'
                  }}
                >
                  Vessel Name
                </span>
                <span 
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 500,
                    fontStyle: 'normal',
                    fontSize: '12px',
                    lineHeight: '20px',
                    letterSpacing: '0px',
                    color: '#212126'
                  }}
                >
                  {shipmentData.vesselName}
                </span>
              </div>
            )}

            {/* Voyage Number */}
            {shipmentData.voyageNumber && (
              <div className="flex items-center py-3 border-b border-gray-100" style={{ gap: '40px' }}>
                <span 
                  className="flex-shrink-0"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 300,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '22px',
                    letterSpacing: '0px',
                    color: '#69697A',
                    width: '140px'
                  }}
                >
                  Voyage Number
                </span>
                <span 
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 500,
                    fontStyle: 'normal',
                    fontSize: '12px',
                    lineHeight: '20px',
                    letterSpacing: '0px',
                    color: '#212126'
                  }}
                >
                  {shipmentData.voyageNumber}
                </span>
              </div>
            )}

            {/* Cargo Description */}
            {shipmentData.cargoDescription && (
              <div className="flex items-center py-3 border-b border-gray-100" style={{ gap: '40px' }}>
                <span 
                  className="flex-shrink-0"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 300,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '22px',
                    letterSpacing: '0px',
                    color: '#69697A',
                    width: '140px'
                  }}
                >
                  Cargo Description
                </span>
                <span 
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 500,
                    fontStyle: 'normal',
                    fontSize: '12px',
                    lineHeight: '20px',
                    letterSpacing: '0px',
                    color: '#212126'
                  }}
                >
                  {shipmentData.cargoDescription}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div 
          className="flex items-center justify-end gap-4 border-t border-gray-200 flex-shrink-0"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2',
            paddingLeft: '24px',
            paddingRight: '24px',
            borderBottomLeftRadius: '12px'
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 500,
              fontSize: '14px',
              color: '#212126',
              backgroundColor: '#FFFFFF'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default ShipmentDetailsModal

